import React, { useState } from 'react';

import { Col, Form, Row, Select } from 'antd';
import { useParams } from 'react-router-dom';

import {
  CustomNotification,
  StyledModal,
  WithLoading,
  WrapperSelect,
} from 'common/components';

import {
  GroupAssignAllergens,
  GroupAssignFactsPanels,
  GroupAssignIngredients,
} from '../../shared/GroupAssignParts';

import { GroupName } from './GroupLayout';

import { useAssignGroupProduct, useGetGroupDetail } from './hooks';

import { appendDataGroup } from '../../shared/utils';

import { mappingAllergenGroupsWhenEditing } from 'pages/qa-spec/components/group-data/utils';

import './GroupLayout.less';

const defaultModalProps = {
  destroyOnClose: true,
  width: 1140,
  maskClosable: false,
  bodyStyle: {
    minHeight: '60vh',
    maxHeight: '70vh',
  },
};

export const GroupAssign = (props) => {
  const {
    defaultGroupId,
    selectedGroupIds = [],
    productGroups = [],
    onCancel: offModal,
    onAssignSuccess,
    additionalIngredientsGroups = [],
    additionalAllergenGroups = [],
    additionalNutritionFacts = [],
    additionalSupplementFacts = [],
    additionalDrugFacts = [],
    additionalPetFood = [],
    ...rest
  } = props;

  const { id: productId } = useParams();

  const [formInstance] = Form.useForm();

  const [groupId, setGroupId] = useState(defaultGroupId);

  const assignGroupMutation = useAssignGroupProduct(productId, groupId);

  const { groupDetail, isLoading } = useGetGroupDetail({
    productId,
    groupId,
  });

  const {
    ingredientGroups = [],
    allergenGroups = [],
    nutritionFacts = [],
    supplementFacts = [],
    drugFacts = [],
    petNutritionFacts = [],
  } = groupDetail ?? {};

  const appendedIngredientGroups = appendDataGroup(
    additionalIngredientsGroups,
    ingredientGroups
  );

  const appendedAllergenGroups = appendDataGroup(
    additionalAllergenGroups,
    allergenGroups
  );

  const appendedNutritionFacts = appendDataGroup(
    additionalNutritionFacts,
    nutritionFacts
  );

  const appendedSupplementGroups = appendDataGroup(
    additionalSupplementFacts,
    supplementFacts
  );

  const appendedDrugFactsGroups = appendDataGroup(
    additionalDrugFacts,
    drugFacts
  );

  const appendedPetFoodGroups = appendDataGroup(
    additionalPetFood,
    petNutritionFacts
  );

  // Ordered
  const appendedFactsPanels = [
    ...appendDataGroup(
      additionalNutritionFacts,
      additionalSupplementFacts,
      additionalDrugFacts,
      additionalPetFood
    ),
    ...appendDataGroup(
      nutritionFacts,
      supplementFacts,
      drugFacts,
      petNutritionFacts
    ),
  ];

  const filterProductGroups = productGroups.filter(
    (group) => !selectedGroupIds.includes(group.groupId)
  );

  const handleAssignGroup = () => {
    const newSupplementFactIds = appendedSupplementGroups.map(
      (supplement) => supplement.id
    );

    const newNutritionFactIds = appendedNutritionFacts.map(
      (nutrition) => nutrition.id
    );

    const newDrugFactIds = appendedDrugFactsGroups.map(
      (drugFacts) => drugFacts.id
    );

    const newPetFoodIds = appendedPetFoodGroups.map((petFood) => petFood.id);

    const newAllergenGroupIndices = appendedAllergenGroups.map(
      (allergen) => allergen.index
    );

    const newIngredientGroupIndices = appendedIngredientGroups.map(
      (ingredient) => ingredient.index
    );

    const params = {
      productItemId: productId,
      groupId,
      ingredientGroupIndices: newIngredientGroupIndices,
      allergenGroupIndices: newAllergenGroupIndices,
      nutritionFactIds: newNutritionFactIds,
      supplementFactIds: newSupplementFactIds,
      drugFactIds: newDrugFactIds,
      petNutritionFactIds: newPetFoodIds,
    };

    assignGroupMutation.mutate(params, {
      onSuccess: (result) => {
        result?.isSuccess &&
          CustomNotification.success('Assign group successfully');
        formInstance.resetFields();
        offModal();
        onAssignSuccess && onAssignSuccess();
      },
    });
  };

  return (
    <StyledModal
      {...defaultModalProps}
      title='Assign Group'
      onCancel={offModal}
      onOk={handleAssignGroup}
      okButtonProps={{
        loading: assignGroupMutation.isLoading,
      }}
      {...rest}
    >
      <WithLoading loading={isLoading}>
        <Form form={formInstance}>
          <GroupName>
            <WrapperSelect
              allowClear={false}
              placeholder='Please select a group name'
              onChange={(value) => setGroupId(value)}
              defaultValue={groupId}
            >
              {filterProductGroups?.map((group) => {
                return (
                  <Select.Option key={group.groupId} value={group.groupId}>
                    {group.groupName}
                  </Select.Option>
                );
              })}
            </WrapperSelect>
          </GroupName>
        </Form>

        <Row gutter={[12, 16]} className='group-layout__assign-item'>
          <Col span={24}>
            <GroupAssignIngredients
              allIngredientGroups={appendedIngredientGroups}
              additionalIngredientGroups={additionalIngredientsGroups}
            />
          </Col>
          <Col span={24}>
            <GroupAssignAllergens
              allAllergenGroups={mappingAllergenGroupsWhenEditing(
                appendedAllergenGroups
              )}
              additionalAllergenGroups={additionalAllergenGroups}
            />
          </Col>
          <Col span={24}>
            <GroupAssignFactsPanels
              factsPanels={appendedFactsPanels}
              additionalDrugFacts={additionalDrugFacts}
              additionalNutritionFacts={additionalNutritionFacts}
              additionalSupplementFacts={additionalSupplementFacts}
              additionalPetFood={additionalPetFood}
            />
          </Col>
        </Row>
      </WithLoading>
    </StyledModal>
  );
};
